/**
 * Formie GCLID Injection Helper
 * 
 * Handles:
 * - Safe GCLID retrieval from localStorage
 * - Form-scoped field injection (avoids wrong form selection)
 * - Formie lifecycle integration (onFormieInit event)
 * - Dynamic form support (modals, Sprig, etc.)
 * - Prevents double injection
 */

import { getGclid } from './gclid.js';

// Track injected forms to prevent double injection
const injectedForms = new WeakSet();

/**
 * Inject GCLID into a specific Formie form instance
 * Idempotent - safe to call multiple times, won't double-inject
 * 
 * @param {HTMLElement|string} formElementOrId - The form element or form ID to inject into
 * @returns {boolean} True if injection was successful, false otherwise
 */
export function injectGclidIntoFormie(formElementOrId) {
    try {
        // Get GCLID value
        const gclid = getGclid();

        if (!gclid) {
            if (window.DEBUG_GCLID) {
                console.warn('[GCLID] No GCLID available for Formie injection');
            }
            return false;
        }

        // Get form element (handle both element and ID)
        let formElement = null;
        if (typeof formElementOrId === 'string') {
            formElement = document.getElementById(formElementOrId) || document.querySelector(`form[id="${formElementOrId}"]`);
        } else {
            formElement = formElementOrId;
        }

        if (!formElement || formElement.tagName !== 'FORM') {
            if (window.DEBUG_GCLID) {
                console.warn('[GCLID] Form element not found:', formElementOrId);
            }
            return false;
        }

        // Prevent double injection - check if already injected
        if (injectedForms.has(formElement)) {
            // Check if field already has the correct value
            const gclidField = formElement.querySelector('[name="fields[gclid]"]');
            if (gclidField && gclidField.value === gclid) {
                if (window.DEBUG_GCLID) {
                    console.log('[GCLID] Form already injected with correct value, skipping:', formElement.id);
                }
                return true;
            }
            // If value changed, allow re-injection
            injectedForms.delete(formElement);
        }

        // Find GCLID field within this specific form (scoped selector)
        const gclidField = formElement.querySelector('[name="fields[gclid]"]');

        if (!gclidField) {
            if (window.DEBUG_GCLID) {
                console.warn('[GCLID] GCLID field not found in form. Form ID:', formElement.id);
            }
            return false;
        }

        // Skip if already has correct value
        if (gclidField.value === gclid) {
            injectedForms.add(formElement);
            return true;
        }

        // Populate the field
        gclidField.value = gclid;
        gclidField.setAttribute('value', gclid);

        // Trigger events to ensure Formie picks up the change
        gclidField.dispatchEvent(new Event('input', { bubbles: true }));
        gclidField.dispatchEvent(new Event('change', { bubbles: true }));

        // Mark as injected
        injectedForms.add(formElement);

        if (window.DEBUG_GCLID) {
            console.log('[GCLID] Successfully injected into Formie form. GCLID:', gclid, 'Form ID:', formElement.id);
        }
        return true;
    } catch (e) {
        console.error('[GCLID] Error injecting GCLID into Formie form:', e);
        return false;
    }
}

/**
 * Simplified form finder - tries common Formie event patterns
 * @param {Event} event - Formie initialization event
 * @returns {HTMLElement|null} Form element or null
 */
function findFormFromEvent(event) {
    // Try common Formie event patterns
    if (event.detail) {
        // Pattern 1: Direct form element
        if (event.detail.form && event.detail.form instanceof HTMLElement) {
            return event.detail.form;
        }
        // Pattern 2: Form ID
        if (event.detail.formId) {
            const form = document.getElementById(event.detail.formId);
            if (form) return form;
        }
        // Pattern 3: Formie factory with form reference
        if (event.detail.formie) {
            if (event.detail.formie.form && event.detail.formie.form instanceof HTMLElement) {
                return event.detail.formie.form;
            }
            if (event.detail.formie.formId) {
                const form = document.getElementById(event.detail.formie.formId);
                if (form) return form;
            }
        }
    }
    // Pattern 4: Event target or closest form
    if (event.target instanceof HTMLElement) {
        if (event.target.tagName === 'FORM') {
            return event.target;
        }
        if (typeof event.target.closest === 'function') {
            return event.target.closest('form');
        }
    }
    return null;
}

/**
 * Find all forms with GCLID fields on the page
 * Optimized: Queries GCLID fields directly instead of scanning all forms
 * @returns {Array<HTMLElement>} Array of form elements
 */
function findAllFormsWithGclid() {
    // Find all GCLID fields directly (more efficient than querying all forms)
    const gclidFields = document.querySelectorAll('[name="fields[gclid]"]');
    const forms = new Set();
    
    // Get parent form for each GCLID field
    gclidFields.forEach(field => {
        const form = field.closest('form');
        if (form) {
            forms.add(form);
        }
    });
    
    return Array.from(forms);
}

/**
 * Setup Formie GCLID injection globally
 * 
 * This function:
 * - Listens for onFormieInit events (Formie lifecycle)
 * - Listens for form submit events (safety net)
 * - Injects into existing forms on page load
 * - Uses lightweight MutationObserver only for modal containers
 */
export function setupFormieGclidInjection() {
    // Prevent double setup
    if (window._gclidFormieSetup) {
        if (window.DEBUG_GCLID) {
            console.warn('[GCLID] Formie injection already setup, skipping');
        }
        return;
    }
    window._gclidFormieSetup = true;

    // Listen for Formie initialization events
    document.addEventListener('onFormieInit', function (event) {
        try {
            if (window.DEBUG_GCLID) {
                console.log('[GCLID] onFormieInit event received');
            }

            const formElement = findFormFromEvent(event);

            if (formElement) {
                // Single injection attempt - function is idempotent
                injectGclidIntoFormie(formElement);
            } else {
                // Fallback: find all forms with GCLID fields
                const forms = findAllFormsWithGclid();
                forms.forEach(form => injectGclidIntoFormie(form));
            }
        } catch (e) {
            console.error('[GCLID] Error in onFormieInit handler:', e);
        }
    });

    // Listen for form submit events (safety net - inject right before submit)
    document.addEventListener('submit', function (event) {
        const form = event.target;
        if (form && form.tagName === 'FORM') {
            const gclidField = form.querySelector('[name="fields[gclid]"]');
            if (gclidField) {
                // Idempotent - safe to call even if already injected
                injectGclidIntoFormie(form);
            }
        }
    }, true); // Capture phase

    // Inject into existing forms on page load
    function injectIntoExistingForms() {
        const forms = findAllFormsWithGclid();
        if (window.DEBUG_GCLID && forms.length > 0) {
            console.log('[GCLID] Injecting into', forms.length, 'existing form(s)');
        }
        forms.forEach(form => injectGclidIntoFormie(form));
    }

    // Run on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', injectIntoExistingForms);
    } else {
        // DOM already loaded
        setTimeout(injectIntoExistingForms, 100);
    }

    // Lightweight MutationObserver with debouncing
    // Only watches for new forms being added (modals, Sprig, etc.)
    // Performance optimization: Only activate if Formie forms exist or modal containers are present
    if (typeof MutationObserver !== 'undefined' && document.body) {
        // Check if Formie forms exist on the page before activating observer
        const existingForms = findAllFormsWithGclid();
        const hasFormieForms = existingForms.length > 0;

        // Find common modal/container elements to scope observer
        const modalContainers = [
            document.querySelector('[data-modal]'),
            document.querySelector('[data-sprig]'),
            document.querySelector('.modal'),
            document.querySelector('#modal'),
            document.querySelector('[class*="modal"]'),
            document.querySelector('[id*="modal"]')
        ].filter(Boolean);

        // Only activate observer if:
        // 1. Formie forms exist on page, OR
        // 2. Modal containers are present (likely to contain dynamic forms)
        if (hasFormieForms || modalContainers.length > 0) {
            let debounceTimeout;
            const observer = new MutationObserver(function (mutations) {
                // Debounce to avoid excessive checks on DOM changes
                clearTimeout(debounceTimeout);
                debounceTimeout = setTimeout(function () {
                    const forms = findAllFormsWithGclid();
                    forms.forEach(form => {
                        // Only inject if not already injected
                        if (!injectedForms.has(form)) {
                            injectGclidIntoFormie(form);
                        }
                    });
                }, 200); // 200ms debounce
            });

            // Prefer scoping to modal containers if available, otherwise observe body
            if (modalContainers.length > 0) {
                // Scope observer to modal containers (more performant)
                modalContainers.forEach(container => {
                    observer.observe(container, {
                        childList: true,
                        subtree: true
                    });
                });

                if (window.DEBUG_GCLID) {
                    console.log('[GCLID] MutationObserver scoped to', modalContainers.length, 'modal container(s)');
                }
            } else {
                // Fallback: observe document.body but only if Formie forms exist
                observer.observe(document.body, {
                    childList: true,
                    subtree: true
                });

                if (window.DEBUG_GCLID) {
                    console.log('[GCLID] MutationObserver active (Formie forms detected)');
                }
            }
        } else {
            // No Formie forms or modal containers - skip observer for better performance
            if (window.DEBUG_GCLID) {
                console.log('[GCLID] MutationObserver skipped (no Formie forms or modal containers detected)');
            }
        }
    }
}
